import { useState, useEffect } from 'react'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'

interface RegionItem {
  region: string
  total: number
  con_match: number
  sin_match: number
  porcentaje: number
  estado: string
}

interface EspecialidadItem {
  especialidad: string
  total: number
  con_match: number
  sin_match: number
  porcentaje: number
  estado: string
}

interface NivelImportancia {
  nombre: string
  descripcion: string
  con_match: number
  total: number
  porcentaje: number
}

interface MedicosEspecialidadesData {
  titulo: string
  subtitulo: string
  total_medicos: number
  con_coincidencia: number
  sin_coincidencia: number
  porcentaje_match: number
  porcentaje_sin_match: number
  regiones: RegionItem[]
  especialidades: EspecialidadItem[]
  acelerador_x: number
  acelerador_y: number
  niveles_importancia?: NivelImportancia[]
  excel_url?: string
}

function getEstadoBadgeClasses(estado: string): string {
  if (estado === 'Alto Match' || estado === 'Excelente') {
    return 'bg-green-100 text-green-800'
  }
  if (estado === 'Bajo Match') {
    return 'bg-yellow-100 text-yellow-800'
  }
  return 'bg-red-100 text-red-800'
}

export function MedicosEspecialidadesPage() {
  const { data, isLoading, error } = useDashReport<MedicosEspecialidadesData>('medicos/especialidades')
  const [regionesOpen, setRegionesOpen] = useState(false)
  const [especialidadesOpen, setEspecialidadesOpen] = useState(false)

  // Auto-expand both sections after 500ms (matching Django template behavior)
  useEffect(() => {
    if (data) {
      const timer = setTimeout(() => {
        setRegionesOpen(true)
        setEspecialidadesOpen(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [data])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const nivelesImportancia = data.niveles_importancia || [
    { nombre: 'Top 20', descripcion: 'médicos más importantes', con_match: 3, total: 20, porcentaje: 15.0 },
  ]

  const excelUrl = data.excel_url || 'https://drive.google.com/uc?export=download&id=1O_fXNtvxJViqM1Y3QWH9SqJ-QO1tJFR1'

  return (
    <div className="bg-gray-100 p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
              </div>
            </div>
            <div className="flex items-center">
              <a href={excelUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-green-600 hover:bg-green-700 text-white text-xs lg:text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm w-full lg:w-auto justify-center">
                <svg className="w-3 h-3 lg:w-4 lg:h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Descargar Excel
              </a>
            </div>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{data.total_medicos}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{data.con_coincidencia}</p>
              <p className="text-sm text-gray-600 mt-1">En Base de Datos</p>
              <p className="text-xs text-green-600 font-semibold mt-1">{data.porcentaje_match}%</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-500">{data.sin_coincidencia}</p>
              <p className="text-sm text-gray-600 mt-1">Sin Coincidencia</p>
              <p className="text-xs text-gray-500 font-semibold mt-1">{data.porcentaje_sin_match}%</p>
            </div>
          </div>
        </div>

        {/* Distribución por Región */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setRegionesOpen(!regionesOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-indigo-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-indigo-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-base lg:text-lg">Distribución por Región</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({data.regiones.length} regiones)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${regionesOpen ? 'rotate-180' : ''}`}
                style={!regionesOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <div className="mt-4" />

          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: regionesOpen ? '9999px' : '0px',
              opacity: regionesOpen ? 1 : 0,
            }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Región</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">En Base</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Sin Match</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="hidden sm:inline">% Match</span>
                      <span className="sm:hidden">Match</span>
                    </th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.regiones.map((region) => (
                    <tr key={region.region} className="hover:bg-gray-50">
                      <td className="px-3 lg:px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="whitespace-nowrap">{region.region}</div>
                        <div className="text-xs text-gray-500 sm:hidden">Total: {region.total}</div>
                      </td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{region.total}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-green-600 font-semibold hidden sm:table-cell">{region.con_match}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 hidden sm:table-cell">{region.sin_match}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center">
                        <span className="text-blue-600 font-bold">{region.porcentaje}%</span>
                        <div className="text-xs text-gray-500 sm:hidden">
                          <span className="text-green-600">{region.con_match}</span>/<span className="text-gray-500">{region.sin_match}</span>
                        </div>
                      </td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-1 lg:px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getEstadoBadgeClasses(region.estado)}`}>
                          {region.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Distribución por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspecialidadesOpen(!especialidadesOpen)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-green-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                <span className="text-base lg:text-lg">Distribución por Especialidad</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({data.especialidades.length} especialidades)</span>
              </div>
              <svg
                className={`w-6 h-6 lg:w-5 lg:h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${especialidadesOpen ? 'rotate-180' : ''}`}
                style={!especialidadesOpen ? { animation: 'pulse-subtle 2s ease-in-out infinite' } : undefined}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>

          <div className="mt-4" />

          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{
              maxHeight: especialidadesOpen ? '9999px' : '0px',
              opacity: especialidadesOpen ? 1 : 0,
            }}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Especialidad</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">En Base</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Sin Match</th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="hidden sm:inline">% Match</span>
                      <span className="sm:hidden">Match</span>
                    </th>
                    <th className="px-2 lg:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.especialidades.map((esp) => (
                    <tr key={esp.especialidad} className="hover:bg-gray-50">
                      <td className="px-3 lg:px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="text-xs lg:text-sm">{esp.especialidad}</div>
                        <div className="text-xs text-gray-500 sm:hidden">Total: {esp.total}</div>
                      </td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{esp.total}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-green-600 font-semibold hidden sm:table-cell">{esp.con_match}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500 hidden sm:table-cell">{esp.sin_match}</td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-sm text-center">
                        <span className="text-blue-600 font-bold">{esp.porcentaje}%</span>
                        <div className="text-xs text-gray-500 sm:hidden">
                          <span className="text-green-600">{esp.con_match}</span>/<span className="text-gray-500">{esp.sin_match}</span>
                        </div>
                      </td>
                      <td className="px-2 lg:px-6 py-4 whitespace-nowrap text-center">
                        <span className={`px-1 lg:px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getEstadoBadgeClasses(esp.estado)}`}>
                          {esp.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Análisis Detallado y Sidebar */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Análisis por Nivel de Importancia (2/3 width) */}
          <div className="lg:col-span-2 bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Análisis por Nivel de Importancia
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Distribución de matches según el ranking de importancia médica. Los médicos están ordenados del 1 al {data.total_medicos}, donde #1 es el más importante.
            </p>

            <div className="space-y-3">
              {nivelesImportancia.map((nivel) => (
                <div key={nivel.nombre} className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 mr-4">
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <span className="font-medium text-gray-900">{nivel.nombre}</span>
                          <span className="text-xs text-gray-500 sm:ml-2">{nivel.descripcion}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <span className="text-green-600 font-semibold">{nivel.con_match}</span>
                        <span className="text-gray-500 text-sm">/</span>
                        <span className="text-gray-700">{nivel.total}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${nivel.porcentaje}%` }} />
                      </div>
                      <span className="font-bold text-blue-600">{nivel.porcentaje.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar dividido en mitades */}
          <div className="grid grid-cols-1 gap-4 h-fit">
            {/* Cobertura Estratégica (mitad superior) */}
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center text-sm">
                <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Cobertura Estratégica
              </h3>
              <div className="p-6 bg-green-100 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 leading-relaxed mb-4">
                  Tenemos <strong>{data.con_coincidencia} de los {data.total_medicos} médicos registrados</strong> ({data.porcentaje_match}% de cobertura total). Esta base permite campañas efectivas dirigidas.
                </p>
                <div className="text-xs text-green-600 mt-4 pt-4 border-t border-green-200">
                  <div className="flex items-center justify-between">
                    <span>Cobertura actual</span>
                    <span className="font-semibold">{data.porcentaje_match}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Indicador de Cobertura (mitad inferior) */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center text-sm">
                <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Indicador de Cobertura
              </h3>

              <div className="text-center py-1">
                {/* Gráfico de acelerador */}
                <div className="relative w-32 h-32 mx-auto">
                  <svg className="w-32 h-32" viewBox="0 0 100 100">
                    {/* Arco de fondo (gris) */}
                    <path d="M 20 50 A 30 30 0 0 1 80 50" stroke="#e5e7eb" strokeWidth="8" fill="none" strokeLinecap="round" />
                    {/* Arco de progreso (azul) */}
                    <path d={`M 20 50 A 30 30 0 0 1 ${data.acelerador_x} ${data.acelerador_y}`} stroke="#3b82f6" strokeWidth="8" fill="none" strokeLinecap="round" />
                  </svg>

                  {/* Número en el centro */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-4xl font-bold text-blue-600">{data.con_coincidencia}</div>
                  </div>
                </div>

                {/* Porcentaje */}
                <div className="text-base font-medium text-blue-400">
                  {data.porcentaje_match}%
                </div>

                {/* Label con ícono médico */}
                <div className="flex items-center justify-center text-sm text-gray-600">
                  <svg className="w-4 h-4 text-blue-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Médicos en Base de Datos
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer sutil */}
      <footer className="mt-12 py-6 text-center">
        <div className="text-xs text-gray-400">
          <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors inline-flex items-center">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Tonal Enterprise
          </a>
          <span className="mx-2">&bull;</span>
          <span>&copy; 2025 Tonal Strategies</span>
        </div>
      </footer>

      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}
